import React, { useEffect, useState } from "react";
import axios from "axios";
import { Drawer, message, Popconfirm, Tag, Divider, Empty } from "antd";
import { DeleteOutlined, EditOutlined, FolderOpenOutlined, SyncOutlined, TeamOutlined } from "@ant-design/icons";
import { ButtonItem, InputItem, TextAreaItem } from "../../../Components/Items";
import SelectUsuariosItem from "../../../Components/SelectUsuariosItem";

function TagsDeCampos({ keys, catalogoPorKey, vacioTexto }) {
  const campos = (keys || []).map((key) => catalogoPorKey?.[key]).filter(Boolean);
  if (campos.length === 0) {
    return <span style={{ fontSize: 12, color: "#bfbfbf" }}>{vacioTexto}</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {campos.map((campo) => (
        <Tag key={campo.key} color="blue">
          {campo.label}
        </Tag>
      ))}
    </div>
  );
}

function GestorPlantillasReporte({
  visible,
  onCancel,
  empresaId,
  headersToken,
  modeloKey,
  catalogoPorKey,
  configuracionActual,
  onAplicar,
  soloCompartidos,
}) {
  const [plantillas, setPlantillas] = useState([]);
  const [propioUsuarioId, setPropioUsuarioId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [nombreNueva, setNombreNueva] = useState("");
  const [descripcionNueva, setDescripcionNueva] = useState("");
  const [compartirCon, setCompartirCon] = useState([]);

  const [editandoCompartirId, setEditandoCompartirId] = useState(null);
  const [compartirEdicion, setCompartirEdicion] = useState([]);
  const [guardandoCompartir, setGuardandoCompartir] = useState(false);

  const [editandoDatosId, setEditandoDatosId] = useState(null);
  const [nombreEdicion, setNombreEdicion] = useState("");
  const [descripcionEdicion, setDescripcionEdicion] = useState("");
  const [guardandoDatos, setGuardandoDatos] = useState(false);

  const [actualizandoId, setActualizandoId] = useState(null);

  const cargarPlantillas = () => {
    if (!modeloKey) return;
    setLoading(true);
    axios
      .post("creadorDeReportes/plantillas/listar", { empresaId, modeloKey }, { headers: headersToken })
      .then((res) => {
        if (res.data.success !== 2) return message.error("No se pudieron cargar las plantillas");
        setPlantillas(res.data.plantillas || []);
        setPropioUsuarioId(res.data.propioUsuarioId);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible) cargarPlantillas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, modeloKey]);

  const guardar = () => {
    if (!nombreNueva.trim()) return message.warning("Ponle un nombre a la plantilla");
    if (!configuracionActual.camposSeleccionados?.length) {
      return message.warning("Elige al menos un campo antes de guardar la plantilla");
    }

    setGuardando(true);
    axios
      .post(
        "creadorDeReportes/plantillas/guardar",
        {
          empresaId,
          modeloKey,
          nombre: nombreNueva.trim(),
          descripcion: descripcionNueva.trim(),
          compartidaConUsuariosIds: compartirCon,
          ...configuracionActual,
        },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return message.error(res.data.message || "No se pudo guardar la plantilla");
        message.success("Plantilla guardada");
        setNombreNueva("");
        setDescripcionNueva("");
        setCompartirCon([]);
        cargarPlantillas();
      })
      .finally(() => setGuardando(false));
  };

  const eliminar = (plantillaId) => {
    axios
      .post("creadorDeReportes/plantillas/eliminar", { plantillaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success !== 2) return message.error(res.data.message || "No se pudo eliminar la plantilla");
        message.success("Plantilla eliminada");
        cargarPlantillas();
      });
  };

  const abrirEdicionCompartir = (plantilla) => {
    setEditandoDatosId(null);
    setEditandoCompartirId(plantilla._id);
    setCompartirEdicion(plantilla.compartidaConUsuariosIds || []);
  };

  const guardarCompartir = (plantillaId) => {
    setGuardandoCompartir(true);
    axios
      .post(
        "creadorDeReportes/plantillas/actualizarCompartidos",
        { plantillaId, compartidaConUsuariosIds: compartirEdicion },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return message.error(res.data.message || "No se pudo actualizar con quién se comparte");
        message.success("Actualizado con quién se comparte");
        setEditandoCompartirId(null);
        cargarPlantillas();
      })
      .finally(() => setGuardandoCompartir(false));
  };

  const abrirEdicionDatos = (plantilla) => {
    setEditandoCompartirId(null);
    setEditandoDatosId(plantilla._id);
    setNombreEdicion(plantilla.nombre);
    setDescripcionEdicion(plantilla.descripcion || "");
  };

  const guardarDatos = (plantillaId) => {
    if (!nombreEdicion.trim()) return message.warning("Ponle un nombre a la plantilla");
    setGuardandoDatos(true);
    axios
      .post(
        "creadorDeReportes/plantillas/editar",
        { plantillaId, nombre: nombreEdicion.trim(), descripcion: descripcionEdicion.trim() },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return message.error(res.data.message || "No se pudo editar la plantilla");
        message.success("Plantilla editada");
        setEditandoDatosId(null);
        cargarPlantillas();
      })
      .finally(() => setGuardandoDatos(false));
  };

  const actualizarConConfigActual = (plantillaId) => {
    setActualizandoId(plantillaId);
    axios
      .post(
        "creadorDeReportes/plantillas/editar",
        { plantillaId, ...configuracionActual },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) return message.error(res.data.message || "No se pudo actualizar la plantilla");
        message.success("Plantilla actualizada con la configuración actual");
        cargarPlantillas();
      })
      .finally(() => setActualizandoId(null));
  };

  return (
    <Drawer title="Plantillas de reporte" visible={visible} onClose={onCancel} width={480}>
      <div style={{ padding: "0" }}>
        {!modeloKey ? (
          <div style={{ marginBottom: 16, fontSize: 12, color: "#595959" }}>
            No tienes acceso a ningún modelo de reporte.
          </div>
        ) : soloCompartidos ? (
          <div style={{ marginBottom: 16, fontSize: 12, color: "#595959" }}>
            Solo puedes aplicar plantillas que otro usuario haya compartido contigo para este modelo.
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
              Guardar la configuración actual como plantilla
            </div>

            <div style={{ marginBottom: 4, fontSize: 12, color: "#595959" }}>
              Campos que se van a guardar ({(configuracionActual.camposSeleccionados || []).length}):
            </div>
            <div
              style={{
                marginBottom: 12,
                padding: "8px 10px",
                backgroundColor: "#fafafa",
                borderRadius: 6,
                border: "1px solid #f0f0f0",
                minHeight: 36,
              }}
            >
              <TagsDeCampos
                keys={configuracionActual.camposSeleccionados}
                catalogoPorKey={catalogoPorKey}
                vacioTexto="No has elegido ningún campo todavía."
              />
            </div>

            <InputItem
              placeholder="Nombre de la plantilla"
              value={nombreNueva}
              onChange={(e) => setNombreNueva(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <TextAreaItem
              placeholder="Descripción (opcional) -- para qué sirve este reporte, cuándo usarlo..."
              value={descripcionNueva}
              onChange={(e) => setDescripcionNueva(e.target.value)}
              rows={2}
              style={{ marginBottom: 8 }}
            />

            <div style={{ marginBottom: 4, fontSize: 12, color: "#595959" }}>
              Compartir con (opcional, vacío = solo tú la ves):
            </div>
            <SelectUsuariosItem
              empresaId={empresaId}
              headersToken={headersToken}
              value={compartirCon}
              onChange={setCompartirCon}
            />

            <ButtonItem type="primary" verde loading={guardando} onClick={guardar} style={{ marginTop: 12, width: "100%" }}>
              Guardar como plantilla nueva
            </ButtonItem>
          </>
        )}

        <Divider style={{ margin: "16px 0" }} />

        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Plantillas guardadas</div>
        {!loading && plantillas.length === 0 && <Empty description="Sin plantillas guardadas" />}
        {plantillas.map((plantilla) => {
          const esDueño = plantilla.usuarioId === propioUsuarioId;
          const editandoCompartirEsta = editandoCompartirId === plantilla._id;
          const editandoDatosEsta = editandoDatosId === plantilla._id;

          return (
            <div key={plantilla._id} style={{ padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {plantilla.nombre}{" "}
                    {esDueño ? (
                      plantilla.compartidaConUsuariosIds?.length > 0 && <Tag color="cyan">Compartida por ti</Tag>
                    ) : (
                      <Tag color="purple">Compartida contigo</Tag>
                    )}
                  </div>
                  {plantilla.descripcion && (
                    <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2 }}>{plantilla.descripcion}</div>
                  )}
                </div>
                <ButtonItem size="small" cyan icon={<FolderOpenOutlined />} onClick={() => onAplicar(plantilla)}>
                  Aplicar
                </ButtonItem>
                {esDueño && (
                  <Popconfirm
                    title="¿Actualizar esta plantilla con los campos/filtros que tienes armados ahora?"
                    onConfirm={() => actualizarConConfigActual(plantilla._id)}
                  >
                    <ButtonItem size="small" naranja icon={<SyncOutlined />} loading={actualizandoId === plantilla._id} />
                  </Popconfirm>
                )}
                {esDueño && (
                  <ButtonItem
                    size="small"
                    lima
                    icon={<EditOutlined />}
                    onClick={() => (editandoDatosEsta ? setEditandoDatosId(null) : abrirEdicionDatos(plantilla))}
                  />
                )}
                {esDueño && (
                  <ButtonItem
                    size="small"
                    blanco
                    icon={<TeamOutlined />}
                    onClick={() => (editandoCompartirEsta ? setEditandoCompartirId(null) : abrirEdicionCompartir(plantilla))}
                  />
                )}
                {esDueño && (
                  <Popconfirm title="¿Eliminar esta plantilla?" onConfirm={() => eliminar(plantilla._id)}>
                    <ButtonItem size="small" rojo icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </div>

              <div style={{ marginTop: 6 }}>
                <TagsDeCampos keys={plantilla.camposSeleccionados} catalogoPorKey={catalogoPorKey} vacioTexto="Sin campos" />
              </div>

              {editandoDatosEsta && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    backgroundColor: "#fafafa",
                    borderRadius: 6,
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <InputItem
                    placeholder="Nombre de la plantilla"
                    value={nombreEdicion}
                    onChange={(e) => setNombreEdicion(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <TextAreaItem
                    placeholder="Descripción"
                    value={descripcionEdicion}
                    onChange={(e) => setDescripcionEdicion(e.target.value)}
                    rows={2}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    <ButtonItem size="small" onClick={() => setEditandoDatosId(null)}>
                      Cancelar
                    </ButtonItem>
                    <ButtonItem
                      size="small"
                      type="primary"
                      verde
                      loading={guardandoDatos}
                      onClick={() => guardarDatos(plantilla._id)}
                    >
                      Guardar
                    </ButtonItem>
                  </div>
                </div>
              )}

              {editandoCompartirEsta && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    backgroundColor: "#fafafa",
                    borderRadius: 6,
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Compartir con:</div>
                  <SelectUsuariosItem
                    empresaId={empresaId}
                    headersToken={headersToken}
                    value={compartirEdicion}
                    onChange={setCompartirEdicion}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
                    <ButtonItem size="small" onClick={() => setEditandoCompartirId(null)}>
                      Cancelar
                    </ButtonItem>
                    <ButtonItem
                      size="small"
                      type="primary"
                      verde
                      loading={guardandoCompartir}
                      onClick={() => guardarCompartir(plantilla._id)}
                    >
                      Guardar
                    </ButtonItem>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}

export default GestorPlantillasReporte;
